import React, { ReactElement, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import CheckIcon from '@mui/icons-material/Check'
import { useForm } from '@tanstack/react-form'
import { useAppDispatch } from 'src/store/hooks'
import {
  updatePendingCAPCE,
  updatePendingCAPCECourse,
  fetchEventAccreditations,
  fetchCourseAccreditations,
} from 'src/v3/content-pipeline/accreditation.slice'
import axios from 'src/utilities/http-client'
import { modalPaperProps } from 'src/v3/utilities/modal-styles'
import { StatusField } from '../shared'
import {
  TopicAreaField,
  ClassTypeField,
  DateField,
  NotesField,
  DateFieldsRow,
  DateFieldsRowThree,
} from './form-fields'
import { CAPCEPendingAccreditation, CAPCEFinalizedAccreditation } from './types'

type EditFormProps = {
  eventId?: string
  courseId?: string
  pending: CAPCEPendingAccreditation
  finalized?: CAPCEFinalizedAccreditation
  open: boolean
  setOpen: (isOpen: boolean) => void
  onSuccess: () => void
}

export let EditForm = ({
  eventId,
  courseId,
  pending,
  finalized,
  open,
  setOpen,
  onSuccess,
}: EditFormProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [generatedAnccCode, setGeneratedAnccCode] = useState<string | null>(null)
  let [generateLoading, setGenerateLoading] = useState(false)
  let [generateError, setGenerateError] = useState<string | null>(null)
  let [copied, setCopied] = useState(false)

  let handleGenerateCode = async (setFieldValue: (val: string) => void) => {
    setGenerateLoading(true)
    setGenerateError(null)
    setGeneratedAnccCode(null)
    try {
      let url = courseId
        ? `/api/content-pipeline/accreditation-course/pending-capce/${courseId}/generate-code`
        : `/api/content-pipeline/accreditation-event/pending-capce/${eventId}/generate-code`
      let { data } = await axios.get<{ capceCode: string; anccCode: string }>(
        url,
      )
      setFieldValue(data.capceCode)
      setGeneratedAnccCode(data.anccCode)
    } catch {
      setGenerateError('Failed to generate code. Ensure Class Type is set.')
    } finally {
      setGenerateLoading(false)
    }
  }

  let handleCopyAncc = () => {
    if (!generatedAnccCode) return
    navigator.clipboard.writeText(generatedAnccCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  let form = useForm({
    defaultValues: {
      status: pending.status,
      courseCode: pending.courseCode || finalized?.courseCode || '',
      expirationDate: pending.expirationDate || finalized?.expirationDate || '',
      topicArea: pending.topicArea || finalized?.topicArea || '',
      classType: pending.classType || finalized?.classType || '',
      applicationDate:
        pending.applicationDate || finalized?.applicationDate || '',
      submissionDate: pending.submissionDate || '',
      allClearDate: pending.allClearDate || finalized?.allClearDate || '',
      goLiveDate: pending.goLiveDate || finalized?.goLiveDate || '',
      notes: pending.notes || '',
    },
    onSubmit: ({ value }) => {
      let payload = {
        id: pending.id,
        status: value.status,
        courseCode: value.courseCode || null,
        expirationDate: value.expirationDate || null,
        topicArea: value.topicArea,
        classType: value.classType,
        applicationDate: value.applicationDate || null,
        submissionDate: value.submissionDate || null,
        allClearDate: value.allClearDate || null,
        notes: value.notes || null,
      }
      if (courseId) {
        dispatch(updatePendingCAPCECourse({ courseId, ...payload }))
        dispatch(fetchCourseAccreditations(courseId))
      } else if (eventId) {
        dispatch(
          updatePendingCAPCE({
            eventId,
            ...payload,
            goLiveDate: value.goLiveDate || null,
          }),
        )
        dispatch(fetchEventAccreditations(eventId))
      }
      setOpen(false)
      onSuccess()
    },
  })

  let handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.handleSubmit()
  }

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      slotProps={{
        paper: {
          ...modalPaperProps,
          sx: { ...modalPaperProps.sx, borderRadius: 2 },
        },
      }}
      maxWidth="md"
    >
      <form onSubmit={handleFormSubmit}>
        <DialogTitle>Edit Pending CAPCE Accreditation</DialogTitle>
        <DialogContent>
          <Box className="grid gap-6 mt-2">
            <Typography color="text.secondary" variant="body2">
              Update the pending CAPCE accreditation details.
            </Typography>

            <form.Field
              name="status"
              children={(field) => <StatusField field={field} />}
            />

            <form.Field
              name="courseCode"
              children={(field) => (
                <Box className="grid gap-2">
                  <TextField
                    label="Course Code"
                    name={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    fullWidth
                    sx={{ bgcolor: 'background.paperDark' }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Generate course code">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleGenerateCode(field.handleChange)
                                  }
                                  disabled={generateLoading}
                                >
                                  {generateLoading ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <AutoFixHighIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  {generateError && (
                    <Alert severity="error" sx={{ py: 0 }}>
                      {generateError}
                    </Alert>
                  )}
                  {generatedAnccCode && (
                    <TextField
                      label="ANCC Code (copy to use in ANCC portal)"
                      value={generatedAnccCode}
                      fullWidth
                      slotProps={{
                        input: {
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip
                                title={copied ? 'Copied!' : 'Copy ANCC code'}
                              >
                                <IconButton
                                  size="small"
                                  onClick={handleCopyAncc}
                                >
                                  {copied ? (
                                    <CheckIcon
                                      fontSize="small"
                                      color="success"
                                    />
                                  ) : (
                                    <ContentCopyIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  )}
                </Box>
              )}
            />

            <DateFieldsRow>
              <form.Field
                name="topicArea"
                children={(field) => <TopicAreaField field={field} />}
              />
              <form.Field
                name="classType"
                children={(field) => <ClassTypeField field={field} />}
              />
            </DateFieldsRow>

            <DateFieldsRowThree>
              <form.Field
                name="applicationDate"
                children={(field) => (
                  <DateField field={field} label="Application Date" />
                )}
              />
              <form.Field
                name="submissionDate"
                children={(field) => (
                  <DateField field={field} label="Submission Date" />
                )}
              />
              <form.Field
                name="expirationDate"
                children={(field) => (
                  <DateField field={field} label="Expiration Date" />
                )}
              />
            </DateFieldsRowThree>

            {eventId ? (
              <DateFieldsRow>
                <form.Field
                  name="allClearDate"
                  children={(field) => (
                    <DateField field={field} label="All Clear Date" />
                  )}
                />
                <form.Field
                  name="goLiveDate"
                  children={(field) => (
                    <DateField field={field} label="Go Live Date" />
                  )}
                />
              </DateFieldsRow>
            ) : (
              <form.Field
                name="allClearDate"
                children={(field) => (
                  <DateField field={field} label="All Clear Date" />
                )}
              />
            )}

            <form.Field
              name="notes"
              children={(field) => <NotesField field={field} />}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} type="button">
            Cancel
          </Button>
          <Button type="submit" variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
